import type { CarrierOption, ManualOrderImportResult } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, apiBlob, errorMessage } from '../../api';
import { downloadBlob, fileToBase64 } from '../../files';
import { fmt } from '../../format';
import { useSession } from '../../session';
import { Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Many manual orders at once: download the Excel template (or export CSV from another system),
 * check it — nothing is created while a row has a problem — then create them all.
 */
export function OrderImport() {
  const toast = useToast();
  const { warehouses } = useSession();
  const carriers = useLoad(() => api<CarrierOption[]>('GET', '/api/orders/carriers'), []);
  const usable = (carriers.data ?? []).filter((c) => c.available);
  const [carrierCode, setCarrierCode] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ManualOrderImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (carrierCode === null && usable.length > 0) setCarrierCode((usable.find((c) => c.isDefault) ?? usable[0]!).code);
  }, [carriers.data]);

  const template = async () => {
    try {
      downloadBlob(await apiBlob('/api/orders/import-template'), 'siparis-yukleme-sablonu.xlsx');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const run = async (dryRun: boolean, chosen = file) => {
    if (!chosen) return;
    if (chosen.size > MAX_BYTES) return setError('Dosya 5 MB üzerinde; daha az satırla deneyin');
    setBusy(true);
    setError(null);
    try {
      const answer = await api<ManualOrderImportResult>('POST', `/api/orders/import?dryRun=${dryRun}`, {
        warehouseId,
        carrierCode,
        fileName: chosen.name,
        contentBase64: await fileToBase64(chosen),
      });
      setResult(answer);
      if (!answer.dryRun) toast(`${answer.created.length} sipariş oluşturuldu`, 'ok');
    } catch (e) {
      setError(errorMessage(e));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const imported = result && !result.dryRun;
  const canImport = result && result.dryRun && result.errors.length === 0 && result.orders > 0;

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/siparisler">
          <Icon name="back" /> Siparişler
        </a>
        <a className="btn ghost" href="#/siparisler/yeni">
          <Icon name="plus" /> Tek sipariş gir
        </a>
      </div>
      <section className="card">
        <div className="card-b stack">
          <div className="steps-list">
            <div>
              <b>1. Şablonu indirin.</b> Her satır bir sipariş kalemidir; aynı sipariş numaralı satırlar tek sipariş olur, numarasız her satır ayrı bir
              sipariştir. Alıcı bilgilerini çok ürünlü siparişin yalnızca ilk satırına yazmanız yeterli.
            </div>
            <div>
              <button className="btn" onClick={() => void template()}>
                <Icon name="download" /> Excel şablonunu indir
              </button>
            </div>
            <div>
              <b>2. Kargo firmasını seçin.</b> "Kargo" sütunu boş bırakılan siparişler bununla gönderilir; etiketler sipariş oluşur oluşmaz hazırlanır.
            </div>
            {!carriers.data ? (
              <Spinner />
            ) : (
              <div className="row">
                {usable.map((c) => (
                  <button key={`${c.code}|${c.shippingModel}`} className={`chip select${carrierCode === c.code ? ' on' : ''}`} onClick={() => setCarrierCode(c.code)}>
                    {c.name}
                    {c.sandbox && ' · test'}
                  </button>
                ))}
                {warehouses.length > 1 && (
                  <select className="input" style={{ maxWidth: 220 }} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} aria-label="Gönderen depo">
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div>
              <b>3. Dosyayı seçin ve kontrol edin.</b> Hiçbir satırda sorun kalmayana kadar sipariş oluşturulmaz.
            </div>
            <div className="row">
              <input
                type="file"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={!carrierCode}
                onChange={(e) => {
                  const chosen = e.target.files?.[0] ?? null;
                  setFile(chosen);
                  setResult(null);
                  if (chosen) void run(true, chosen);
                }}
              />
              {file && (
                <button className="btn sm" disabled={busy} onClick={() => void run(true)}>
                  <Icon name="refresh" size={16} /> Yeniden kontrol et
                </button>
              )}
            </div>
          </div>
          {error && <div className="banner bad">{error}</div>}
          {busy && <div className="muted">Dosya okunuyor…</div>}
        </div>
      </section>

      {result && (
        <section className="card">
          <div className="card-h">
            <h2>{imported ? 'Siparişler oluşturuldu' : 'Kontrol sonucu'}</h2>
            <span className="sub">{file?.name}</span>
          </div>
          <div className="card-b stack">
            <div className="grid">
              <div className="kpi">
                <span className="n">{fmt.n(result.rows)}</span>
                <span className="l">Satır</span>
              </div>
              <div className="kpi">
                <span className="n">{fmt.n(result.orders)}</span>
                <span className="l">Sipariş</span>
              </div>
              <div className="kpi">
                <span className="n">{fmt.n(result.units)}</span>
                <span className="l">Adet</span>
              </div>
              <div className={`kpi${result.errors.length > 0 ? ' alert' : ''}`}>
                <span className="n">{fmt.n(new Set(result.errors.map((e) => e.row)).size)}</span>
                <span className="l">Sorunlu satır</span>
              </div>
            </div>
            {result.ignoredColumns.length > 0 && <div className="banner warn">Tanınmayan sütunlar okunmadı: {result.ignoredColumns.join(', ')}</div>}
            {imported && (
              <div className="banner ok">
                <Icon name="check" />
                <div>
                  {result.created.length} sipariş oluşturuldu; stokları ayrılıyor, kargo etiketleri hazırlanıyor. <a href="#/siparisler">Siparişlere git</a>
                </div>
              </div>
            )}
            {canImport && (
              <div className="row">
                <button className="btn primary" disabled={busy} onClick={() => void run(false)}>
                  <Icon name="upload" /> {result.orders} siparişi oluştur
                </button>
              </div>
            )}
            {result.errors.length > 0 && (
              <>
                <h3>Düzeltilmesi gerekenler</h3>
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th className="num">Satır</th>
                        <th>Sütun</th>
                        <th>Sorun</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.slice(0, 300).map((e, i) => (
                        <tr key={i}>
                          <td className="num">{e.row}</td>
                          <td>{e.column ?? '—'}</td>
                          <td>{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <h3>Siparişler</h3>
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th className="num">Satır</th>
                    <th>Sipariş no</th>
                    <th>Alıcı</th>
                    <th>Şehir</th>
                    <th className="num">Kalem</th>
                    <th className="num">Adet</th>
                    <th>Kargo</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {result.preview.map((p) => (
                    <tr key={p.rows}>
                      <td className="num">{p.rows}</td>
                      <td className="mono">{p.orderNumber ?? <span className="muted">otomatik</span>}</td>
                      <td>{p.customerName || '—'}</td>
                      <td>{p.city || '—'}</td>
                      <td className="num">{p.lines}</td>
                      <td className="num">{p.units}</td>
                      <td>{p.carrier ?? '—'}</td>
                      <td>
                        <span className={`chip ${p.action === 'create' ? 'ok' : 'bad'}`}>{p.action === 'create' ? (imported ? 'Oluşturuldu' : 'Hazır') : 'Hatalı'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
